import { Injectable, Renderer2, RendererFactory2, effect, inject, signal } from '@angular/core';
import { AuthService } from './auth.service';
import { LocalStorageService } from './local-storage.service';
import { ColorMode } from '../Types/color-mode.type';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly _rendererFactory = inject(RendererFactory2);
  private readonly _authService = inject(AuthService);
  private readonly _localStorage = inject(LocalStorageService);
  private readonly _renderer: Renderer2 = this._rendererFactory.createRenderer(null, null);

  private readonly THEME_KEY = 'preferred_theme';

  readonly mode = signal<ColorMode>('light');

  constructor() {
    this._registerDomSync();
    this._registerOperatorSync();
  }

  toggleMode(): void {
    const next: ColorMode = this.mode() === 'light' ? 'dark' : 'light';

    this.mode.set(next);
    this._localStorage.setItem(this.THEME_KEY, next);
  }

  private _registerDomSync(): void {
    effect(() => this._applyToDom(this.mode()));
  }

  private _registerOperatorSync(): void {
    this._authService.currentOperator$.subscribe((operator) =>
      this.mode.set(this._resolveMode(operator?.preferred_theme ?? null)),
    );
  }

  private _resolveMode(preferred: string | null): ColorMode {
    if (this._isColorMode(preferred)) {
      return preferred;
    }

    const stored = this._localStorage.getItem(this.THEME_KEY);

    return this._isColorMode(stored) ? stored : 'light';
  }

  private _isColorMode(value: string | null): value is ColorMode {
    return value === 'light' || value === 'dark';
  }

  private _applyToDom(mode: ColorMode): void {
    const previous = mode === 'light' ? 'dark-theme' : 'light-theme';

    this._renderer.removeClass(document.body, previous);
    this._renderer.addClass(document.body, `${mode}-theme`);
  }
}
